import React, {useEffect, useMemo, useState} from 'react'
import {formatPriceNl} from "../../config/toolKit";

const EMPTY_LIST = []

const unitLabel = (unit) => {
    if (unit === 'day') return 'per dag'
    if (unit === 'hour') return 'per uur'
    if (unit === 'reservation') return 'eenmalig'
    if (unit === 'infinite') return 'eenmalig'
    return unit || ''
}


const ReservationOptionsPanel = ({
    vehicleInfo,
    options,
    products,
    defaultProductRentalDays = 1,
    showPricesInclVat = true,
    onSelectionChange
}) => {
    const [selected, setSelected] = useState([])
    const [productQuantities, setProductQuantities] = useState({})
    const [productRentalDays, setProductRentalDays] = useState({})
    const [infoOption, setInfoOption] = useState(null)

    const optionList = useMemo(() => {
        if (Array.isArray(vehicleInfo?.options)) return vehicleInfo.options
        if (Array.isArray(options)) return options
        return EMPTY_LIST
    }, [vehicleInfo, options])

    const productList = useMemo(() => {
        if (Array.isArray(vehicleInfo?.products)) return vehicleInfo.products
        if (Array.isArray(products)) return products
        return EMPTY_LIST
    }, [vehicleInfo, products])

    useEffect(() => {
        const initialSelected = optionList.map((option) =>
            Boolean(
                option.activeByDefault ||
                option.active_by_default ||
                option.is_popular ||
                option.isPopular
            )
        )
        setSelected(initialSelected)
    }, [optionList, onSelectionChange])

    useEffect(() => {
        const nextQuantities = {}
        productList.forEach((product) => {
            nextQuantities[product.id] = 0
        })
        setProductQuantities(nextQuantities)
    }, [productList])

    const toggleOption = (index) => {
        setSelected((prev) => {
            return prev.map((value, current) => (current === index ? !value : value))
        })
    }

    const setProductQty = (productId, value) => {
        const nextValue = Math.max(0, Math.floor(Number(value) || 0))
        setProductQuantities((prev) => ({
            ...prev,
            [productId]: nextValue
        }))
    }

    const setProductDays = (productId, value) => {
        const nextValue = Math.max(1, Math.floor(Number(value) || 1))
        setProductRentalDays((prev) => ({
            ...prev,
            [productId]: nextValue
        }))
    }

    useEffect(() => {
        if (!onSelectionChange) return
        const selectedIds = optionList
            .filter((_, index) => selected[index])
            .map((option) => option.id)
            .filter(Boolean)
        const selectedProducts = Object.entries(productQuantities).reduce((carry, [productId, quantity]) => {
            const parsedProductId = Number(productId)
            const parsedQuantity = Number(quantity)
            if (!Number.isFinite(parsedProductId) || parsedProductId <= 0) return carry
            if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) return carry
            const selectedDays = Number(productRentalDays?.[parsedProductId])
            const normalizedDays = Number.isFinite(selectedDays) && selectedDays > 0
                ? Math.floor(selectedDays)
                : Math.max(1, Math.floor(Number(defaultProductRentalDays) || 1))
            carry[parsedProductId] = {
                quantity: Math.floor(parsedQuantity),
                days: normalizedDays
            }
            return carry
        }, {})
        onSelectionChange({
            options: selectedIds,
            products: selectedProducts
        })
    }, [selected, optionList, productQuantities, productRentalDays, defaultProductRentalDays, onSelectionChange])

    const onInfoClick = (event, option) => {
        event.preventDefault()
        event.stopPropagation()
        setInfoOption(option)
    }

    const getOptionPrice = (option) => {
        const preferredValue = Number(
            showPricesInclVat ? option?.price_including_vat : option?.price_excluding_vat
        )
        if (Number.isFinite(preferredValue)) return preferredValue

        const fallbackValue = Number(
            showPricesInclVat ? option?.price_excluding_vat : option?.price_including_vat
        )
        if (Number.isFinite(fallbackValue)) return fallbackValue

        const priceAmount = Number(option?.price?.amount)
        return Number.isFinite(priceAmount) ? priceAmount : null
    }

    const getProductPrice = (product) => {
        const preferredValue = Number(
            showPricesInclVat
                ? (product?.rental_price_including_vat ?? product?.sales_price_including_vat)
                : (product?.rental_price_excluding_vat ?? product?.sales_price_excluding_vat)
        )
        if (Number.isFinite(preferredValue)) return preferredValue

        const fallbackValue = Number(
            showPricesInclVat
                ? (product?.rental_price_excluding_vat ?? product?.sales_price_excluding_vat)
                : (product?.rental_price_including_vat ?? product?.sales_price_including_vat)
        )
        return Number.isFinite(fallbackValue) ? fallbackValue : null
    }

    useEffect(() => {
        const normalizedDefaultDays = Math.max(1, Math.floor(Number(defaultProductRentalDays) || 1))
        setProductRentalDays((prev) => {
            const nextDays = {}
            productList.forEach((product) => {
                const prevDays = Number(prev?.[product.id])
                nextDays[product.id] = Number.isFinite(prevDays) && prevDays > 0
                    ? Math.floor(prevDays)
                    : normalizedDefaultDays
            })
            return nextDays
        })
    }, [defaultProductRentalDays, productList])

    return (
        <div className="kav-options">
            <h2 className="kav-options__title">Extra opties</h2>
            <div className="kav-options__list">
                {optionList.map((option, index) => {
                    const isChecked = Boolean(selected[index])
                    const isPopular = Boolean(option.is_popular ?? option.isPopular)
                    const priceAmount = getOptionPrice(option)
                    const priceUnit = option.unit ?? option.price?.unit
                    const subtitle = option.subtitle
                    return (
                        <label
                            key={`${option.id ?? option.name}-${index}`}
                            className={`kav-option ${isChecked ? 'is-active' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleOption(index)}
                            />
                            <span className="kav-option__box" aria-hidden="true">
                                <i className="bi bi-check-lg"></i>
                            </span>
                            <span className="kav-option__body">
                                <span className="kav-option__name">
                                    {option.name}
                                    {
                                        option.description ?
                                            <button
                                                type="button"
                                                className="kav-option__infoBtn"
                                                onClick={(event) => onInfoClick(event, option)}
                                                aria-label={`Info over ${option.name}`}
                                            >
                                                <i className="bi bi-info-circle"></i>
                                            </button>
                                            :
                                            ''
                                    }
                                </span>
                                {subtitle ? (
                                    <span className="kav-option__sub">{subtitle}</span>
                                ) : null}
                            </span>
                            <span className="kav-option__price">
                                <strong>{formatPriceNl(priceAmount)}</strong>
                                <span>{unitLabel(priceUnit)}</span>
                            </span>
                            {isPopular && (
                                <span className="kav-option__badge">
                                    <i className="bi bi-fire"></i>
                                    Populaire optie
                                </span>
                            )}
                        </label>
                    )
                })}
            </div>

            {productList.length > 0 && (
                <>
                    <h3 className="kav-options__subtitle">Beschikbare producten</h3>
                    <div className="kav-products__list">
                        {productList.map((product) => {
                            const quantity = Number(productQuantities?.[product.id]) || 0
                            const rentalDays = Number(productRentalDays?.[product.id]) || Math.max(1, Math.floor(Number(defaultProductRentalDays) || 1))
                            const priceAmount = getProductPrice(product)
                            const priceUnit = product.rental_unit ?? 'reservation'
                            return (
                                <div className="kav-product" key={`product-${product.id}`}>
                                    <div className="kav-product__body">
                                        <span className="kav-product__name">{product.name}</span>
                                        <span className="kav-product__price">
                                            <strong>{formatPriceNl(priceAmount)}</strong>
                                            <span>{unitLabel(priceUnit)}</span>
                                        </span>
                                    </div>
                                    <div className="kav-product__qty">
                                        <button
                                            type="button"
                                            className="kav-product__qtyBtn"
                                            onClick={() => setProductQty(product.id, quantity - 1)}
                                            aria-label={`Minder ${product.name}`}
                                        >
                                            <i className="bi bi-dash-lg"></i>
                                        </button>
                                        <input
                                            className="kav-product__qtyInput"
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={quantity}
                                            onChange={(event) => setProductQty(product.id, event.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="kav-product__qtyBtn"
                                            onClick={() => setProductQty(product.id, quantity + 1)}
                                            aria-label={`Meer ${product.name}`}
                                        >
                                            <i className="bi bi-plus-lg"></i>
                                        </button>
                                    </div>
                                    <div className="kav-product__days">
                                        <span className="kav-product__daysLabel">Dagen</span>
                                        <div className="kav-product__daysControl">
                                            <button
                                                type="button"
                                                className="kav-product__qtyBtn"
                                                onClick={() => setProductDays(product.id, rentalDays - 1)}
                                                aria-label={`Minder dagen voor ${product.name}`}
                                            >
                                                <i className="bi bi-dash-lg"></i>
                                            </button>
                                            <input
                                                className="kav-product__daysInput"
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={rentalDays}
                                                onChange={(event) => setProductDays(product.id, event.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="kav-product__qtyBtn"
                                                onClick={() => setProductDays(product.id, rentalDays + 1)}
                                                aria-label={`Meer dagen voor ${product.name}`}
                                            >
                                                <i className="bi bi-plus-lg"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </>
            )}

            {infoOption && (
                <div className="kav-option-modal" role="dialog" aria-modal="true">
                    <div className="kav-option-modal__backdrop" onClick={() => setInfoOption(null)} />
                    <div className="kav-option-modal__card">
                        <div className="kav-option-modal__header">
                            <h3>{infoOption.name}</h3>
                            <button
                                type="button"
                                className="kav-option-modal__close"
                                onClick={() => setInfoOption(null)}
                                aria-label="Sluiten"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <p>{infoOption.description || infoOption.mainDescription}</p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ReservationOptionsPanel
